import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { useSubscriptionTranslationsContext } from "../context/SubscriptionsTranslationsContext/useSubscriptionTranslationsContext";

import { SubscriptionsItem } from "../components/Subscriptions/SubscriptionsItem";
import { MySubscriptions } from "../components/Subscriptions/MySubscriptions";
import { NotificationAlerts } from "../components/Notifications/NotificationAlerts";
import { Link, Header, Alert, LoadingIcon } from "../components/UI";
import { HelperUtils, SubscriptionService } from "../services";
import {
    SubscriptionsHeader,
    SubscriptionsHeader2,
} from "../components/Subscriptions/SubscriptionsHeader";
import "./SubscriptionsPage.css";
import { useCommonTranslations, useRegionContext } from "tds_shared_ui";

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { user, addToCart, dismissNotification } = useUserContext();
  const { translate: translateCommon } = useCommonTranslations();
  const { translate: translateSubscriptions } = useSubscriptionTranslationsContext();
  const { locale, regionData } = useRegionContext();

  const [state, setState] = useState({
    dirtyCart: false,
    loading: false,
    showNotification: false,
    alert: {
      display: false,
      type: "default",
      message: "",
    },
    allSubscriptions: null,
    programmingSub: [],
    diagnosticsSub: [],
    serviceInfoSub: [],
    techlineConnectSub: [],
    dataSecuritySub: [],
    showProgrammingSubscription: true,
    showDiagnosticsSubscription: true,
    showServiceInfoSubscription: true,
    showTlcSubscription: true,
    showDataSecuritySubscription: true,
    showEPCOLSubscription: true,
    showSPTools: true,
  });

  const alertRef = useRef();
  const getSubscriptionsCancelToken = useRef(null);

  const applyRegionVisibility = useCallback((regionCode) => {
    const code = regionCode?.toUpperCase() || "";
    setState((prev) => ({
      ...prev,
      showTlcSubscription: code === "US" || code === "CA",
      showDataSecuritySubscription: code === "AU",
      showEPCOLSubscription: code === "CN",
      showSPTools: code === "CN",
    }));
  }, []);

  const scrollToAlert = useCallback(() => {
    HelperUtils.scrollToAlert(alertRef);
  }, []);

  const linkToKeyCodesBtn = useCallback(() => {
    navigate("/keycodes");
  }, [navigate]);

  const purchaseSubscribeBtn = useCallback((item, productId) => {
    return new Promise((resolve, reject) => {
      if (!user) {
        setState((prev) => ({
          ...prev,
          alert: {
            display: true,
            message: (
              <span>
                {translateSubscriptions("ALERT_LOGIN_TO_CHECKOUT")} { !user && (
                  <Link to="/registration">{translateSubscriptions("ALERT_LINK_TO_SIGNUP")}</Link>
                )}
              </span>
            ),
            type: "error",
          },
        }));
        scrollToAlert();
        reject({ error: true });
      } else {
        let cartItem = {
          skuid: item,
          quantity: 1,
          productId: productId,
        };
        addToCart(cartItem)
          .then((data) => {
            resolve(data);
            navigate("/shoppingcart");
          })
          .catch((error) => {
            setState((prev) => ({
              ...prev,
              loading: false,
              alert: {
                display: true,
                message: translateSubscriptions("ERROR_ADD_TO_CART"),
                type: "error",
              },
            }));
            scrollToAlert();
            reject(error);
          });
      }
    });
  }, [user, addToCart, navigate, translateSubscriptions, scrollToAlert]);

  const cancelFetch = useCallback(() => {
    if (getSubscriptionsCancelToken.current) {
      getSubscriptionsCancelToken.current.cancel({ unmounted: true });
    }
  }, []);

  const fetchSubscriptions = useCallback(() => {
    setState((prev) => ({
      ...prev,
      loading: true,
      allSubscriptions: null,
      programmingSub: [],
      diagnosticsSub: [],
      serviceInfoSub: [],
      techlineConnectSub: [],
      dataSecuritySub: [],
    }));
    getSubscriptionsCancelToken.current = SubscriptionService.getCancelToken().source();
    SubscriptionService.getSubscriptions({
      cancelToken: getSubscriptionsCancelToken.current.token,
      headers: {
        "Accept-Language": locale.replace("_", "-"),
      },
    })
      .then((data) => {
        const programming = data.filter((cat) => cat.category === "PROGRAMMING");
        const diagnostics = data.filter((cat) => cat.category === "DIAGNOSTICS");
        const serviceInfo = data.filter((cat) => cat.category === "SERVICE_INFORMATION");
        const techlineConnect = data.filter((cat) => cat.category === "TECHLINE_CONNECT");
        const dataSecurity = data.filter((cat) => cat.category === "DATA_SECURITY");
        let i = serviceInfo.length;
        while (i--) {
          if (serviceInfo[i].name === "General Motors Service Repair Manual Information (SI)") {
            serviceInfo.splice(i, 1);
            break;
          }
        }
        setState((prev) => ({
          ...prev,
          loading: false,
          alert: { display: false },
          allSubscriptions: data,
          showProgrammingSubscription: programming.length > 0,
          programmingSub: programming,
          showDiagnosticsSubscription: diagnostics.length > 0,
          diagnosticsSub: diagnostics,
          showServiceInfoSubscription: serviceInfo.length > 0,
          serviceInfoSub: serviceInfo,
          showTlcSubscription: techlineConnect.length > 0,
          techlineConnectSub: techlineConnect,
          dataSecuritySub: dataSecurity,
        }));
      })
      .catch((error) => {
        if (!error.unmounted) {
          const msg = error.translatedMessage || error.internalMessage;
          setState((prev) => ({
            ...prev,
            loading: false,
            alert: {
              display: true,
              message: msg,
              type: "error",
            },
          }));
          scrollToAlert();
        }
      });
  }, [locale, scrollToAlert]);

  useEffect(() => {
    fetchSubscriptions();
    setState((prev) => ({ ...prev, showNotification: true }));
    return () => cancelFetch();
  }, [user, locale, regionData?.regionCode, fetchSubscriptions, cancelFetch]);

  useEffect(() => {
    if (regionData?.regionCode) {
      applyRegionVisibility(regionData.regionCode);
    }
  }, [regionData?.regionCode, applyRegionVisibility]);

  const handleDismiss = useCallback((notification) => {
    if (notification) {
      dismissNotification(notification);
    }
  }, [dismissNotification]);

  if (state.loading) {
    return <LoadingIcon display={state.loading} size="3x" />;
  }

  return (
    <div className="subscriptionsPage">
      {state.showNotification && <NotificationAlerts onDismiss={handleDismiss} />}
      {state.alert.display && (
        <Alert ref={alertRef} type={state.alert.type} className="mt-3">
          {state.alert.message}
        </Alert>
      )}
      <SubscriptionsHeader
        header={translateSubscriptions("HEADER_TDS_TITLE")}
        text={translateSubscriptions("MESSAGE_TDS")}
      />
      <SubscriptionsHeader2
        header={translateCommon("LABEL_SUBSCRIPTIONS")}
        dangerouslySetInnerHTML={{ __html: translateSubscriptions("MESSAGE_SUBSCRIPTIONS") }}
        className="pb-1"
      />
      {user && state.allSubscriptions && (
        <MySubscriptions
          key={regionData?.regionCode}
          allSubscriptions={state.allSubscriptions}
          onPurchaseClick={purchaseSubscribeBtn}
        />
      )}
      <div className="subscriptionsPage">
        {state.showProgrammingSubscription && (
          <Header level={3} className="itemHeader">
            {translateSubscriptions("HEADER_PROGRAMMING")}
          </Header>
        )}
        {state.showProgrammingSubscription && (
          <div>
            <LoadingIcon display={state.loading} text={translateCommon("MESSAGE_LOADING")} />
            {state.programmingSub.map((programmingItem) => (
              <SubscriptionsItem
                loading={state.loading}
                className="programmingSubItems"
                subscriptionType={programmingItem}
                key={programmingItem.id}
                onClick={purchaseSubscribeBtn}
              />
            ))}
          </div>
        )}
        {state.showDiagnosticsSubscription && (
          <Header level={3} className="itemHeader">
            {translateSubscriptions("HEADER_DIAGNOSTICS")}
          </Header>
        )}
        {state.showDiagnosticsSubscription && (
          <div>
            <LoadingIcon display={state.loading} text={translateCommon("MESSAGE_LOADING")} />
            {state.diagnosticsSub.map((diagnosticsItem) => (
              <SubscriptionsItem
                loading={state.loading}
                className="diagnosticsSubItem"
                subscriptionType={diagnosticsItem}
                key={diagnosticsItem.id}
                onClick={purchaseSubscribeBtn}
              />
            ))}
          </div>
        )}
        {state.showServiceInfoSubscription && (
          <Header level={3} className="itemHeader">
            {translateSubscriptions("HEADER_INFORMATION")}
          </Header>
        )}
        {state.showServiceInfoSubscription && (
          <div>
            <LoadingIcon display={state.loading} text={translateCommon("MESSAGE_LOADING")} />
            {state.serviceInfoSub.map((serviceInfoItem) => (
              <SubscriptionsItem
                loading={state.loading}
                className="serviceInfoSubItem"
                subscriptionType={serviceInfoItem}
                key={serviceInfoItem.id}
                onClick={purchaseSubscribeBtn}
              />
            ))}
          </div>
        )}
        {state.showDataSecuritySubscription && (
          <Header level={3} className="itemHeader">
            {translateSubscriptions("HEADER_DATA_SECURITY")}
          </Header>
        )}
        {state.showDataSecuritySubscription && (
          <div>
            <LoadingIcon display={state.loading} text={translateCommon("MESSAGE_LOADING")} />
            {state.dataSecuritySub.map((dataSecurityItem) => (
              <SubscriptionsItem
                loading={state.loading}
                className="dataSecuritySubItem"
                subscriptionType={dataSecurityItem}
                key={dataSecurityItem.id}
                onClick={linkToKeyCodesBtn}
              />
            ))}
          </div>
        )}
        {state.showTlcSubscription && (
          <Header level={3} className="itemHeader">
            {translateSubscriptions("HEADER_TECHLINE_CONNECT")}
          </Header>
        )}
        {state.showTlcSubscription && (
          <div>
            <LoadingIcon display={state.loading} text={translateCommon("MESSAGE_LOADING")} />
            {state.techlineConnectSub.map((techlineConnetItem) => (
              <SubscriptionsItem
                loading={state.loading}
                className="techlineConnectSubItem"
                subscriptionType={techlineConnetItem}
                key={techlineConnetItem.id}
                onClick={purchaseSubscribeBtn}
              />
            ))}
          </div>
        )}
      </div>
      {state.showEPCOLSubscription && (
        <Header level={3} className="itemHeader">
          {translateSubscriptions("HEADER_EPCOL")}
        </Header>
      )}
      {state.showEPCOLSubscription && (
        <div className="text-gray" key="EPCOL">
          {translateSubscriptions("MESSAGE_EPCOL")}
        </div>
      )}
      {state.showSPTools && (
        <Header level={3} className="itemHeader">
          {translateSubscriptions("HEADER_SP_TOOLS")}
        </Header>
      )}
      {state.showSPTools && (
        <div className="text-gray" key="SPTools">
          {translateSubscriptions("MESSAGE_SP_TOOLS")}
        </div>
      )}
      {!state.showSPTools && (
        <p className="fa-sm mt-2 mb-2">
          <i>
            {translateSubscriptions("SUBSCRIPTION_CURRENCY") +
              (state.showDataSecuritySubscription ? " and exclude gst" : "")}
          </i>
        </p>
      )}
      {state.showDataSecuritySubscription ? (
        <p className="fa-sm mt-2 mb-2">
          <i>{translateSubscriptions("SUBSCRIPTION_TEXT_AU")}</i>
        </p>
      ) : (
        ""
      )}
    </div>
  );
};

export default SubscriptionsPage;